/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 *      keep in sync with the code):
 *   role:      lib — classifies a rejection (or an empty 2xx) from
 *              POST /ecommerce_confirm into the four outcomes the checkout must
 *              treat differently: the cart is genuinely stale, the backend
 *              refused on the merits, the session expired, or the outcome is
 *              simply not known.
 *   exports:   CONFIRM_FAILURE, classifyConfirmError(error), confirmErrorDetail(error)
 *   callers:   controllers/checkout.js (checkoutTask, the only caller — the order
 *              confirmation is posted from nowhere else)
 *   tests:     checkout-order-test.js ("checkoutTask …" failure branches),
 *              confirm-error-test.js
 *   gotchas:   `beebeeAjaxPost` (beebee-base/lib/utility/ajax) REJECTS on every
 *              non-2xx with `status` + `responseJSON` on the Error, and rejects
 *              with `status: 0` when `fetch` itself threw (offline, DNS, CORS,
 *              dropped connection). `status: 0` is therefore AMBIGUOUS: the
 *              request may have reached the backend and been processed with the
 *              answer lost on the way back. Only `navigator.onLine === false`
 *              proves the request never left the device.
 *
 * WHY THIS EXISTS (POP-349): checkoutTask used to collapse every rejection into
 * "Some products are no longer available" and empty the cart. The cart lives in
 * sessionStorage and is its own only copy, so a dropped connection destroyed the
 * customer's basket and told them a lie about why. The backend already sends the
 * real reason with a distinct status and message; this module reads it.
 */

/**
 * Verbatim 422 message the API sends when its availability re-check found a
 * product genuinely gone:
 * `api_beebeeboard/src/controllers/scuolasci/ecommerce/checkout/checkout-confirm/confirm-new.ts`
 * (`MSG_NO_DISPO`). It is the ONLY refusal that invalidates the cart — the other
 * 422 of that endpoint ("Ore di lezione sovrapposte nel carrello") means the
 * customer has to move a lesson, not to start over, so the cart must survive it.
 *
 * Both 422s are built with the same `param` ("No dispo") and therefore the same
 * JSON:API `source`: the message text is the only discriminator the API offers.
 * The comparison below is accent/case/whitespace insensitive so a cosmetic
 * rewording upstream degrades to "keep the cart", never to "destroy it".
 */
const MSG_NO_DISPO = 'Alcuni dei prodotti selezionati non sono più disponibili';

/** The outcomes `checkoutTask` has to tell apart. */
export const CONFIRM_FAILURE = Object.freeze({
  /** 422 + MSG_NO_DISPO — products really are gone; the cart is stale. */
  UNAVAILABLE: 'unavailable',
  /** 400/422 — the backend refused on the merits. No order, no payment, cart still valid. */
  REFUSED: 'refused',
  /** 401 — the session expired. The session service logs out and redirects. */
  UNAUTHORIZED: 'unauthorized',
  /** 4xx we did not get past the edge — the request was never processed. Plain retry. */
  TRANSPORT: 'transport',
  /** status 0 / 408 / 5xx, or an empty 2xx — the order MAY exist. Never retry silently. */
  UNKNOWN: 'unknown',
});

/** Accent/case/whitespace-insensitive form used to match the backend message. */
function normalize(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * The message the API meant for the customer, or null when it sent none.
 * JSON:API error objects here carry the same text in `detail` and `title`
 * (see `error422`/`error400` in api_beebeeboard/src/utility/errors.ts); `detail`
 * is the field the rest of this app already reads, `title` is the fallback.
 * @param {*} error - rejection from beebeeAjaxPost
 * @returns {string|null}
 */
export function confirmErrorDetail(error) {
  const first = error?.responseJSON?.errors?.[0];
  const text = first?.detail || first?.title;

  return typeof text === 'string' && text.trim() !== '' ? text.trim() : null;
}

/**
 * True only when the browser knows for certain the request never left the
 * device. `navigator.onLine === true` proves nothing (a LAN with no route out
 * still reports online), so this is used to DE-escalate an ambiguous failure,
 * never to escalate one.
 */
function isDefinitelyOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * Classify a /ecommerce_confirm failure.
 *
 * @param {*} error - the rejection from `beebeeAjaxPost` (an Error carrying
 *   `status` and `responseJSON`), or any thrown value.
 * @returns {{kind: string, status: number, detail: string|null, emptyCart: boolean,
 *            orderMayExist: boolean}}
 *   `emptyCart` is true for the single case in which the cart is provably no
 *   longer valid. `orderMayExist` is true when the backend may have placed the
 *   order anyway, which makes an unguarded retry a double booking.
 */
export function classifyConfirmError(error) {
  const rawStatus = Number(error?.status);
  const status = Number.isFinite(rawStatus) ? rawStatus : 0;
  const detail = confirmErrorDetail(error);

  // Session expired. `beebeeAjaxGet/Post` already announced it on
  // UNAUTHORIZED_EVENT and the session service logs out and redirects: here we
  // only have to say so truthfully and leave the cart alone.
  if (status === 401) {
    return {
      kind: CONFIRM_FAILURE.UNAUTHORIZED,
      status,
      detail,
      emptyCart: false,
      orderMayExist: false,
    };
  }

  // The endpoint's business refusals. Both cancel every partial booking
  // (`safeEndCheckout`) before answering, so no order and no payment exist.
  if (status === 422) {
    const unavailable = normalize(detail) === normalize(MSG_NO_DISPO);

    return {
      kind: unavailable ? CONFIRM_FAILURE.UNAVAILABLE : CONFIRM_FAILURE.REFUSED,
      status,
      detail,
      emptyCart: unavailable,
      orderMayExist: false,
    };
  }

  if (status === 400) {
    return {
      kind: CONFIRM_FAILURE.REFUSED,
      status,
      detail,
      emptyCart: false,
      orderMayExist: false,
    };
  }

  // No answer at all (status 0), a request the server gave up waiting for (408),
  // or a server/gateway failure (5xx): the backend may have created the booking
  // and the payment and lost the answer on the way back. The one exception is a
  // browser that knows it is offline — then the request never went out.
  if (status === 0 && isDefinitelyOffline()) {
    return {
      kind: CONFIRM_FAILURE.TRANSPORT,
      status,
      detail,
      emptyCart: false,
      orderMayExist: false,
    };
  }

  if (status === 0 || status === 408 || status >= 500) {
    return { kind: CONFIRM_FAILURE.UNKNOWN, status, detail, emptyCart: false, orderMayExist: true };
  }

  // Any other 4xx (403 · 404 · 409 · 429 …): the request was rejected before the
  // order logic ran. Nothing was booked, the cart is untouched, retry is safe.
  return {
    kind: CONFIRM_FAILURE.TRANSPORT,
    status,
    detail,
    emptyCart: false,
    orderMayExist: false,
  };
}

/** JSON for a log line, never throwing on a cyclic or exotic value. */
export function safeJson(value) {
  try {
    return JSON.stringify(value ?? null);
  } catch {
    return String(value);
  }
}

/**
 * What the customer is told, and what happens to the cart, for a classified
 * /ecommerce_confirm failure (POP-349). Pure on purpose: the old code decided
 * this inside a catch block, where the only reachable outcome was "everything
 * failed the same way and the basket is gone".
 *
 * Returns `{ messageKey, detail, emptyCart, blockRetry }`: the caller shows
 * `detail` when the API sent one, and the localized `messageKey` otherwise.
 */
export function confirmFailureNotice(outcome) {
  // We never learned whether the backend placed the order. Saying "it failed"
  // would be a guess, and letting the button re-post would risk a second
  // booking and a second charge: keep the cart, say exactly that, and make any
  // retry an explicit, separate decision.
  if (outcome?.orderMayExist) {
    return { messageKey: 'checkout.order outcome unknown', emptyCart: false, blockRetry: true };
  }

  // The session service is already logging out on UNAUTHORIZED_EVENT.
  if (outcome?.kind === CONFIRM_FAILURE.UNAUTHORIZED) {
    return {
      messageKey: 'checkout.session expired during order',
      emptyCart: false,
      blockRetry: false,
    };
  }

  // The ONLY outcome that may empty the cart: the backend re-checked
  // availability and the products are gone, so the cart cannot be bought.
  if (outcome?.kind === CONFIRM_FAILURE.UNAVAILABLE) {
    return {
      messageKey: 'Some products are no longer available',
      detail: outcome.detail,
      emptyCart: true,
      blockRetry: false,
    };
  }

  // A refusal on the merits (overlapping lesson hours, a stored-procedure
  // error…), or a transport failure that never reached the order logic. Either
  // way nothing was charged and the cart is still what the customer wants to
  // buy: show the reason the API sent, when it sent one, and let them retry.
  return {
    messageKey: 'checkout.order not completed retry',
    detail: outcome?.kind === CONFIRM_FAILURE.REFUSED ? outcome.detail : null,
    emptyCart: false,
    blockRetry: false,
  };
}
